import { z } from 'zod';

/** YYYY-MM format for monthly snapshots */
const MonthString = z.string().regex(/^\d{4}-\d{2}$/);

// ---------------------------------------------------------------------------
// Waterfall
// ---------------------------------------------------------------------------

export const MrrWaterfallEntrySchema = z.object({
  month: MonthString,
  openingMrr: z.number(),
  freshMrr: z.number(),
  expansionMrr: z.number(),
  reactivationMrr: z.number(),
  contractionMrr: z.number(),
  churnedMrr: z.number(),
  closingMrr: z.number(),
  netFreshMrr: z.number(),
});
export type MrrWaterfallEntry = z.infer<typeof MrrWaterfallEntrySchema>;

// ---------------------------------------------------------------------------
// Customer counts
// ---------------------------------------------------------------------------

export const MrrCustomerCountsSchema = z.object({
  month: MonthString,
  activeCustomers: z.number().int(),
  freshCustomers: z.number().int(),
  churnedCustomers: z.number().int(),
  reactivatedCustomers: z.number().int(),
});
export type MrrCustomerCounts = z.infer<typeof MrrCustomerCountsSchema>;

// ---------------------------------------------------------------------------
// Retention
// ---------------------------------------------------------------------------

export const MrrRetentionSchema = z.object({
  month: MonthString,
  netMrrRetentionPct: z.number(),
  grossChurnPct: z.number(),
  voluntaryChurnPct: z.number(),
  involuntaryChurnPct: z.number(),
});
export type MrrRetention = z.infer<typeof MrrRetentionSchema>;

// ---------------------------------------------------------------------------
// Cohort retention curves
// ---------------------------------------------------------------------------

export const MrrCohortEntrySchema = z.object({
  cohortMonth: MonthString,
  monthsAfterAcquisition: z.number().int().nonnegative(),
  retainedMrrPct: z.number(),
});
export type MrrCohortEntry = z.infer<typeof MrrCohortEntrySchema>;

// ---------------------------------------------------------------------------
// Revenue concentration
// ---------------------------------------------------------------------------

export const MrrTierBreakdownSchema = z.object({
  tierName: z.string(),
  customerCount: z.number().int(),
  mrrAmount: z.number(),
});
export type MrrTierBreakdown = z.infer<typeof MrrTierBreakdownSchema>;

export const MrrConcentrationSchema = z.object({
  month: MonthString,
  top1Pct: z.number(),
  top5Pct: z.number(),
  top10Pct: z.number(),
  tiers: z.array(MrrTierBreakdownSchema),
});
export type MrrConcentration = z.infer<typeof MrrConcentrationSchema>;

// ---------------------------------------------------------------------------
// Acquisition funnel
// ---------------------------------------------------------------------------

export const MrrFunnelSchema = z.object({
  month: MonthString,
  checkoutSessions: z.number().int(),
  completedCheckouts: z.number().int(),
  checkoutConversionPct: z.number(),
  trialStarts: z.number().int(),
  trialConversions: z.number().int(),
  trialToPaidPct: z.number(),
  avgDaysToFirstPayment: z.number(),
});
export type MrrFunnel = z.infer<typeof MrrFunnelSchema>;

// ---------------------------------------------------------------------------
// Payment health
// ---------------------------------------------------------------------------

export const MrrPaymentHealthSchema = z.object({
  month: MonthString,
  failedPayments: z.number().int(),
  failedPaymentAmount: z.number(),
  recoveredPayments: z.number().int(),
  dunningRecoveryPct: z.number(),
  avgRecoveryTimeDays: z.number(),
  unrecoveredMrr: z.number(),
  refundCount: z.number().int(),
  refundAmount: z.number(),
  refundRatePct: z.number(),
});
export type MrrPaymentHealth = z.infer<typeof MrrPaymentHealthSchema>;

// ---------------------------------------------------------------------------
// Monthly snapshot (composite)
// ---------------------------------------------------------------------------

export const MrrMonthlySnapshotSchema = z.object({
  month: MonthString,
  waterfall: MrrWaterfallEntrySchema,
  customers: MrrCustomerCountsSchema,
  retention: MrrRetentionSchema,
  cohorts: z.array(MrrCohortEntrySchema),
  concentration: MrrConcentrationSchema,
  funnel: MrrFunnelSchema,
  paymentHealth: MrrPaymentHealthSchema,
});
export type MrrMonthlySnapshot = z.infer<typeof MrrMonthlySnapshotSchema>;

// ---------------------------------------------------------------------------
// API response DTO
// ---------------------------------------------------------------------------

export const MrrAnalyticsResponseDTOSchema = z.object({
  org: z.string(),
  months: z.array(MrrMonthlySnapshotSchema),
  hasStripeKey: z.boolean(),
  isMockData: z.boolean(),
});
export type MrrAnalyticsResponseDTO = z.infer<
  typeof MrrAnalyticsResponseDTOSchema
>;
